import { Form } from 'react-bootstrap';
import { When } from "react-if";
import type { FormInputProps } from "_types/form";
import { PasswordInput } from "_components/form/inputs/PasswordInput";
import { CheckInput } from "_components/form/inputs/CheckInput";
import { SelectInput } from "_components/form/inputs/SelectInput";
import classnames from "classnames";
import { PhoneInput } from "_components/form/inputs/PhoneInput";
import { CepInput } from "_components/form/inputs/CepInput";
import { get } from "react-hook-form";
import { CpfInput } from "_components/form/inputs/CpfInput";

const TextualInput = ({
    type,
    name,
    placeholder,
    endIcon,
    register,
    errors,
    comp,
    rows,
    className,
    refCallback,
    ...otherProps
}: FormInputProps) => {
    const fieldErrors = get(errors, name);
    return (
        <>
            {type === 'password' && endIcon ? (
                <>
                    <PasswordInput
                        name={name}
                        placeholder={placeholder}
                        refCallback={refCallback}
                        errors={errors!}
                        register={register}
                        className={className}
                        {...otherProps}
                    />
                </>
            ) : (
                <>
                    <Form.Control
                        type={type}
                        placeholder={placeholder}
                        name={name}
                        as={comp}
                        id={name}
                        ref={refCallback}
                        className={className}
                        isInvalid={fieldErrors}
                        {...(register ? register(name) : {})}
                        rows={rows}
                        {...otherProps}
                    ></Form.Control>

                    {fieldErrors ? (
                        <Form.Control.Feedback type="invalid" className="d-block">
                            {fieldErrors['message'] as any}
                        </Form.Control.Feedback>
                    ) : null}
                </>
            )}
        </>
    );
};

const FormInput = ({
    label,
    type,
    name,
    placeholder,
    endIcon,
    register,
    errors,
    control,
    className,
    labelClassName,
    containerClass,
    full,
    refCallback,
    children,
    action,
    rows,
    multiple,
    ...otherProps
}: FormInputProps) => {
    const comp = type === 'textarea' ? 'textarea' : type === 'select' ? 'select' : 'input';
    if (full) {
        containerClass = classnames(containerClass, 'w-100');
    }
    const hasEndIcon = endIcon !== undefined ? endIcon : true;

    if (type === 'hidden') {
        return (
            <input type={type} name={name} {...(register ? register(name) : {})} {...otherProps} />
        );
    }

    if (type === 'select') {
        return (
            <Form.Group className={containerClass}>
                <When condition={label}>
                    <Form.Label className={labelClassName}>{label}</Form.Label>
                    {action && action}
                </When>

                <SelectInput
                    type={type}
                    multiple={multiple}
                    control={control}
                    name={name}
                    placeholder={placeholder}
                    refCallback={refCallback}
                    errors={errors}
                    register={register}
                    comp={comp}
                    className={className}
                    children={children}
                    {...otherProps}
                />
            </Form.Group>
        );
    }

    if (type === 'phone') {
        return (
            <Form.Group className={containerClass}>
                <When condition={label}>
                    <Form.Label className={labelClassName}>{label}</Form.Label>
                    {action && action}
                </When>
                <PhoneInput
                    type={type}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    refCallback={refCallback}
                    errors={errors}
                    register={register}
                    control={control}
                    comp={comp}
                    className={className}
                    rows={rows}
                    {...otherProps}
                />
            </Form.Group>
        )
    }

    if (type === 'cep') {
        return (
            <Form.Group className={containerClass}>
                <When condition={label}>
                    <Form.Label className={labelClassName}>{label}</Form.Label>
                    {action && action}
                </When>
                <CepInput
                    type={type}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    refCallback={refCallback}
                    errors={errors}
                    register={register}
                    control={control}
                    comp={comp}
                    className={className}
                    rows={rows}
                    {...otherProps}
                />
            </Form.Group>
        )
    }

    if (type === 'cpf') {
        return (
            <Form.Group className={containerClass}>
                <When condition={label}>
                    <Form.Label className={labelClassName}>{label}</Form.Label>
                    {action && action}
                </When>
                <CpfInput
                    type={type}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    refCallback={refCallback}
                    errors={errors}
                    register={register}
                    control={control}
                    comp={comp}
                    className={className}
                    rows={rows}
                    {...otherProps}
                />
            </Form.Group>
        )
    }

    if (type === 'checkbox' || type === 'radio') {
        return (
            <Form.Group className={containerClass}>
                <CheckInput
                    type={type}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    refCallback={refCallback}
                    errors={errors}
                    register={register}
                    comp={comp}
                    className={className}
                    rows={rows}
                    {...otherProps}
                />
            </Form.Group>
        )
    }


    if (type === 'switch') {
        return (
            <Form.Group className={containerClass}>
                <Form.Switch
                    type={type}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    refCallback={refCallback}
                    errors={errors}
                    register={register}
                    comp={comp}
                    className={className}
                    rows={rows}
                    {...otherProps}
                />
                {/* <CheckInput
                    type={type}
                    label={label}
                    name={name}
                    placeholder={placeholder}
                    refCallback={refCallback}
                    errors={errors}
                    register={register}
                    comp={comp}
                    className={className}
                    rows={rows}
                    {...otherProps}
                /> */}
            </Form.Group>
        )
    }

    return (
        <Form.Group className={containerClass}>
            {label ? (
                <>
                    <Form.Label className={labelClassName}>{label}</Form.Label>
                    {action && action}
                </>
            ) : null}

            <TextualInput
                type={type}
                name={name}
                placeholder={placeholder}
                endIcon={hasEndIcon}
                refCallback={refCallback}
                errors={errors}
                register={register}
                comp={comp}
                className={className}
                rows={rows}
                {...otherProps}
            />
        </Form.Group>
    );
};

export default FormInput;
